// Jest is currently struggling with dual commonjs/esm modules,
/* eslint-disable import/no-named-as-default-member */
import { Context } from "@nuxt/types";
import branch, { type BranchError, type JourneyEvent } from "branch-sdk";
import { v4 } from "uuid";
import Vue from "vue";
import { type RouteMeta } from "vue-router";

import { isAuth0UserToken } from "@/schemes/auth0";
import { $config } from "@/utils/accessors/config";
import { includesAllItems } from "@/utils/comparators/array";
import { BranchJourneys } from "@/utils/enums/branch";
import { HOUR } from "@/utils/enums/time";
import { isTiktokIAB } from "@/utils/iab-identification";
import { BRANCH_JOURNEY_DISMISSALS } from "@/utils/localstorage/constants";
import logger, { logLevel } from "@/utils/logger";
import normalizePath from "@/utils/transform/normalize-path";

import { EXPERIMENTS_AUTO_DISMISS_BRANCH_JOURNEY, VARIANT_TREATMENT } from "../experiments/constants";

interface JourneyEventData {
  journey_link_data: {
    openApp: string;
    journey_id: string;
    journey_name: string;
    view_name: string;
    view_id: string;
  };
}

type JourneyEventPatched = JourneyEvent & Partial<{ didCallJourneyDismiss: "didCallJourneyDismiss" }>;

function isJourneyEvent(data: unknown): data is JourneyEventData {
  return data !== null && typeof data === "object" && "journey_link_data" in data;
}

declare module "branch-sdk" {
  interface InitOptions {
    make_new_link?: boolean;
  }

  type ErrorCallback = (err: BranchError) => void;

  // Overload public docs with actual implementation from source:
  // https://github.com/BranchMetrics/web-branch-deep-linking-attribution/blob/master/src/6_branch.js#L953
  // the internal `wrap` method will always support the plucking the callback as the last argument, allowing
  // any missed params to be passed as undefined
  export function track(
    event: string,
    metadata: Record<string, unknown>,
    options: InitOptions,
    callback?: ErrorCallback,
  ): void;
  export function track(event: string, callback?: ErrorCallback): void;

  // Overload with patched event listener
  function addListener(
    event: JourneyEventPatched | undefined,
    listener: (event: JourneyEventPatched, data: object) => void,
  ): void;

  // Overload with patched method for banner dismissal, implementation here:
  // https://github.com/jaredmeakin/web-branch-deep-linking-attribution/pull/1
  function dismissJourney(callback?: (err: BranchError) => void): void;
}

const EXPLORE_JOURNEY_IDS = [BranchJourneys.BETTER_IN_APP, BranchJourneys.GET_MY_FULL_SHOP];

export default class BranchService {
  context!: Context;
  vm!: Vue;
  setHasApp = false;
  vid = v4(); // might have to move
  lastDisplayedBanner: string | undefined;
  dismissedJourneysMap: Record<string, number>;
  initState: "pending" | "success" | "error" = "pending"; // Revert back to a simple property

  constructor(context: Context) {
    this.context = context;
    this.dismissedJourneysMap = this.getDismissedJourneysFromStorage();

    const loggedInWatcher = (newValue: boolean) => {
      if (newValue === true) {
        const user = context.$auth.user;

        if (user === null) {
          return;
        }

        const userId = isAuth0UserToken(user) ? user["http://shopltk.com/profile"].sigilUserId : user.userId;

        branch.setIdentity(userId);
      }
    };

    const branchCloseEventCallback = (_event: JourneyEvent, data: unknown): void => {
      if (!isJourneyEvent(data)) {
        context.$amplitude?.throttledSendEvent("/close-branch-banner", { "dismissal-type": "user-dismissed" });
        return;
      }

      this.dismissedJourneysMap[data.journey_link_data.journey_id] = Date.now();

      context.$amplitude?.throttledSendEvent("/close-branch-banner", {
        "branch-journey": data.journey_link_data.journey_name,
        "branch-banner-name": data.journey_link_data.view_name,
        "dismissal-type": "user-dismissed",
      });
    };

    const branchDismissEventCallback = (_event: JourneyEventPatched, data: unknown): void => {
      if (!isJourneyEvent(data)) {
        context.$amplitude?.throttledSendEvent("/close-branch-banner", { "dismissal-type": "auto-dismissed" });
        return;
      }

      this.dismissedJourneysMap[data.journey_link_data.journey_id] = Date.now();

      context.$amplitude?.throttledSendEvent("/close-branch-banner", {
        "branch-journey": data.journey_link_data.journey_name,
        "branch-banner-name": data.journey_link_data.view_name,
        "dismissal-type": "auto-dismissed",
      });
    };

    branch.init(
      $config.branch.key,
      {
        no_journeys: isTiktokIAB(),
        disable_entry_animation: true,
        disable_exit_animation: true,
        metadata: {
          $vid: this.vid,
          $visit_origin: "web",
        },
      },
      (error, sessionData) => {
        if (error) {
          logger(logLevel.ERROR, "Error initializing the Branch SDK", { error });
          console.error("Error initializing the Branch SDK", error);
          this.initState = "error";
          return;
        }
        this.initState = "success";
        // Until this app switches to Vue3, this is the way for plugins/external modules to make reactive watchers
        this.vm = new Vue({
          // Using the auth $state object so the reactivity is maintained into the vue instance
          data: { $auth: context.$auth },
          watch: {
            "$data.$auth.loggedIn": {
              handler: loggedInWatcher,
              immediate: true,
            },
          },
        });

        // Initialize Journey deeplink params, otherwise tries to deeplink to `open`
        branch.setBranchViewData({ data: this.getBranchViewData() });

        branch.addListener("didCallJourneyDismiss" as JourneyEventPatched, branchDismissEventCallback);
        branch.addListener("didClickJourneyClose" as JourneyEvent, branchCloseEventCallback);
        branch.addListener("didClickJourneyContinue" as JourneyEvent, branchCloseEventCallback);
        branch.addListener("didScrollJourneyBackgroundDismiss" as JourneyEvent, branchCloseEventCallback);
        branch.addListener("didClickJourneyBackgroundDismiss" as JourneyEvent, branchCloseEventCallback);
        branch.addListener("didShowJourney" as JourneyEvent, (_event: JourneyEvent, data: unknown) => {
          this.lastDisplayedBanner = (
            data as { journey_link_data: { view_name: string } }
          )?.journey_link_data?.view_name;

          // this is to avoid double sending from both init and pageview, on init we set no_journeys to true or false so can use that key to distinguish
          if ("no_journeys" in (data as { journey_link_data: Record<string, unknown> }) === false) {
            context.$amplitude?.throttledSendEvent("/show-branch-banner", {
              "branch-journey-creative-name": this.lastDisplayedBanner,
            });
            const autoDismissExperiment =
              context.$experiments?.fetchVariant(EXPERIMENTS_AUTO_DISMISS_BRANCH_JOURNEY)?.value === VARIANT_TREATMENT;
            if (this.lastDisplayedBanner === "Small Top Banner" && autoDismissExperiment) {
              if (!isJourneyEvent(data)) {
                return;
              }
              this.setJourneyDismissal(data.journey_link_data.view_id, data.journey_link_data.journey_id);
            }
          }
        });
        branch.addListener("didClickJourneyCTA" as JourneyEvent, (_event: JourneyEvent, data: unknown) => {
          if (!isJourneyEvent(data)) {
            return;
          }

          if (data.journey_link_data.openApp) {
            context.$amplitude?.sendExitEvent("/open-app", {
              "referring-component": "branch-banner",
              "app-click-type": sessionData?.has_app ? "open app" : "click to app store",
              "branch-journey": data.journey_link_data.journey_name,
              "branch-banner-name": data.journey_link_data.view_name,
            });
          } else {
            context.$amplitude?.sendDownloadAppEvent(
              "branch-banner",
              "",
              data.journey_link_data.view_name,
              data.journey_link_data.journey_name,
            );
          }
        });
      },
    );
  }

  appendVidToUrl(url: string) {
    if (url.match(/(\?|&)((vid)+(=[\w-]*))/g)) {
      return url;
    } else if (url.match(/\?([\w-_]+(=[\w-_]*)?(&[\w-_]+(=[\w-_]*)?)*)?$/g)) {
      return url + "&vid=" + this.vid + "&visit_origin=web";
    } else {
      return url + "?vid=" + this.vid + "&visit_origin=web";
    }
  }

  getBranchViewData() {
    const path = normalizePath(this.context.route.fullPath);

    return {
      $deeplink_path: path, // Matching branch docs going forward. Uses absolute path
      app_url: path.substring(1), // Backwards compatibility, trim leading '/'
      $vid: this.vid,
      $visit_origin: "web",
    };
  }

  setJourneyDismissal(viewId: string, journeyId: string) {
    const journeyDismissals = localStorage.getItem(BRANCH_JOURNEY_DISMISSALS);
    const dismissals = journeyDismissals ? JSON.parse(journeyDismissals) : {};
    dismissals[journeyId] = {
      view_id: viewId,
      dismiss_time: Date.now(),
    };
    localStorage.setItem(BRANCH_JOURNEY_DISMISSALS, JSON.stringify(dismissals));
    this.dismissedJourneysMap[journeyId] = Date.now();
  }

  getDismissedJourneysFromStorage(): Record<string, number> {
    try {
      const result: Record<string, number> = {};
      const dismissedJourneys = JSON.parse(localStorage.getItem(BRANCH_JOURNEY_DISMISSALS) ?? "{}");
      for (const key in dismissedJourneys) {
        result[key] = dismissedJourneys[key].dismiss_time;
      }
      return result;
    } catch (error) {
      logger(logLevel.WARN, "Error getting dismissedJourneysFromStorage. Failed to parse JSON.");
      return {};
    }
  }

  async setHasAppUserProp() {
    if (!this.setHasApp) {
      const hasApp = JSON.parse(sessionStorage.getItem("branch_session") ?? "{}")?.has_app;
      if (hasApp !== undefined) {
        const res = await this.context.$amplitude?.setIdentity([{ name: "has-app", value: hasApp }]);
        if (res === true) {
          this.setHasApp = true;
        }
      }
    }
  }

  async closeJourney() {
    await new Promise<void>((resolve, reject) => {
      branch.closeJourney((err, ...rest) => (err ? reject(err) : resolve(...rest)));
    });
  }

  // The primary difference between this method and `branch.closeJourney()`
  // is that this method also drops a cookie which is similar to a dismissal
  // of the banner by a user. In contrast, `branch.closeJourney()` simply
  // closes the banner, and allows it to re-appear to the user when they
  // navigate back to the page during the same, or subsequent, sessions.
  async dismissJourney() {
    // Branch does this automatically when you click dismiss, but does not remove the style if you programmatically dismiss it

    if (this.lastDisplayedBanner === "Small Top Banner") {
      this.clearMargin();
    }
    await new Promise<void>((resolve, reject) => {
      branch.dismissJourney((err, ...rest) => (err ? reject(err) : resolve(...rest)));
    });
  }

  async refreshJourney() {
    if (this.initState === "error") {
      return Promise.resolve("branch-init-failed");
    }

    try {
      await this.closeJourney();
    } catch (error) {}
    this.clearMargin();
    this.setHasAppUserProp();
    if ((this.context.route.meta as RouteMeta[] | undefined)?.[0]?.showJourney === false) {
      return Promise.resolve("skipping");
    }

    branch.setBranchViewData({ data: this.getBranchViewData() });
    await new Promise<void>((resolve, reject) => {
      branch.track(
        "pageview",
        {
          vid: this.vid,
          visit_origin: "web",
        },
        {
          make_new_link: true,
          disable_entry_animation: true,
          disable_exit_animation: true,
        },
        (err, ...rest) => (err ? reject(err) : resolve(...rest)),
      );
    });
  }

  isJourneyDisplayed(): boolean {
    return Boolean(process.client && document.getElementById("branch-banner-iframe"));
  }

  clearMargin() {
    const parentElement = document.querySelector(".branch-journeys-top")?.parentElement;
    if (parentElement && parentElement.style.marginTop === "76px") {
      parentElement.style.marginTop = "0px";
    }
  }

  areAllExploreJourneysDismissed(): boolean {
    const dismissedJourneyIds = Object.keys(this.dismissedJourneysMap).filter((id) =>
      EXPLORE_JOURNEY_IDS.includes(id as BranchJourneys),
    );
    // Check if any existing dismissals are stale
    if (dismissedJourneyIds.some((id) => this.dismissedJourneysMap[id] < Date.now() - HOUR)) {
      return false;
    }
    // Check to ensure all dismissals have fired
    return includesAllItems(dismissedJourneyIds, EXPLORE_JOURNEY_IDS);
  }
}
